import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Sparkles, ArrowLeft, Flame, AlertTriangle, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import { RenderMarkdownInline } from '../lib/markdown';
import PageTitle from '../components/PageTitle';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_GRADIENTS = {
  Ember:    'linear-gradient(135deg, #F97316 0%, #DC2626 100%)',
  Steel:    'linear-gradient(135deg, #64748B 0%, #0F172A 100%)',
  Damascus: 'linear-gradient(135deg, #1E40AF 0%, #7C3AED 100%)',
  // Titanium fades from a metallic titanium silver into the Forge Ledger
  // electric-blue brand colour — keeps Titanium clearly distinct from Steel's
  // mono-slate gradient while still feeling premium/metallic.
  // The bright #E2E8F0 stop near the start is the "shine" that the shimmer
  // animation pans across the border to create the metal-light effect.
  Titanium: 'linear-gradient(135deg, #94A3B8 0%, #E2E8F0 15%, #475569 45%, #0052FF 80%, #1E40AF 100%)',
};

const tierGradientTextStyle = (name) => {
  const grad = TIER_GRADIENTS[name];
  if (!grad) return undefined;
  return {
    backgroundImage: grad,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
  };
};

const foundingLabel = (remaining, cap) => {
  if (remaining == null || cap == null) return null;
  if (remaining <= 0) return 'Founding offer ended — thanks for the love';
  if (remaining >= 90) return `Be one of the first ${cap} founding members`;
  if (remaining >= 30) return `Only ${remaining} founding spots left`;
  return `Almost gone — ${remaining} spot${remaining === 1 ? '' : 's'} remain`;
};

const FoundingOfferProgress = ({ tierKey, remaining, cap }) => {
  if (remaining == null || cap == null) return null;
  const used = cap - remaining;
  const pct = Math.min(100, Math.max(0, (used / cap) * 100));
  const gradientKey = tierKey === 'damascus'
    ? 'Damascus'
    : tierKey === 'titanium'
      ? 'Titanium'
      : 'Steel';
  const exhausted = remaining <= 0;
  const label = foundingLabel(remaining, cap);
  return (
    <div className="mt-3 text-left" data-testid={`founding-progress-${tierKey}`}>
      <div className="flex items-baseline justify-between gap-2 text-[11px] font-medium text-[#475569]">
        <span className={`inline-flex items-center gap-1 uppercase tracking-[0.14em] text-[10px] font-bold ${exhausted ? 'text-[#64748B]' : 'text-[#020617]'}`}>
          <Flame className={`w-3 h-3 ${exhausted ? 'text-[#94A3B8]' : 'text-[#F97316]'}`} />
          {label}
        </span>
      </div>
      <div className={`mt-1.5 h-1.5 w-full rounded-full overflow-hidden ${exhausted ? 'bg-[#F1F5F9]' : 'bg-[#E2E8F0]'}`}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${pct}%`, backgroundImage: TIER_GRADIENTS[gradientKey] }}
        />
      </div>
    </div>
  );
};

const TitaniumComingSoon = ({ price, period, billingCycle, foundingOffer, features = [], annualPill }) => {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || '');
  const [joined, setJoined] = useState(false);
  const [founderNumber, setFounderNumber] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const join = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/waitlist`, { email, tier: 'titanium' });
      setJoined(true);
      setFounderNumber(r.data?.founder_number ?? null);
      const successCopy = r.data?.founder_number
        ? `You're founder #${r.data.founder_number} on the Titanium waitlist.`
        : "You're on the Titanium waitlist. We'll be in touch.";
      toast.success(successCopy);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not add you to the waitlist.';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="titanium-shimmer rounded-2xl p-[3px] flex relative overflow-hidden"
      style={{
        backgroundImage: TIER_GRADIENTS.Titanium,
      }}
      data-testid="pricing-tier-titanium"
    >
      <div className="bg-white rounded-[14px] p-7 flex flex-col w-full relative z-[1]">
        {/* Eyebrow row — same height as the Tier component's eyebrow so the
            Titanium H3 aligns horizontally with Ember/Steel/Damascus. */}
        <div className="h-5 mb-2 flex items-start">
          <div
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider leading-none"
            style={{ backgroundImage: TIER_GRADIENTS.Titanium }}
            data-testid="titanium-coming-soon-badge"
          >
            <Sparkles className="w-3 h-3" />
            Coming soon
          </div>
        </div>
        <h3
          className="text-3xl font-extrabold tracking-tighter leading-none"
          style={tierGradientTextStyle('Titanium')}
          data-testid="pricing-tier-name-titanium"
        >
          Titanium
        </h3>
        <div className="text-xs italic text-[#64748B] mt-1">Pro Premium</div>

        <div className="flex items-baseline gap-1 mt-2">
          <span className="text-4xl font-extrabold text-[#020617]">{price}</span>
          <span className="text-sm text-[#64748B]">/{period}</span>
        </div>
        {annualPill && (
          <div
            className="mt-2 inline-flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857] text-[11px] font-semibold"
            data-testid="pricing-annual-pill-titanium"
          >
            {annualPill}
          </div>
        )}
        {foundingOffer}

        <ul className="space-y-2 my-5 flex-1">
          {features.map((bullet, idx) => (
            // Bullet text is unique within a tier; we use it as the stable key
            // so reordering / inserting a new bullet preserves React state on
            // unchanged siblings. `idx` suffix guards against the edge case
            // where two bullets are byte-identical (e.g. blank dividers).
            <li key={`${bullet}::${idx}`} className="flex items-start gap-2 text-sm text-[#475569]">
              <Check className="w-4 h-4 text-[#0052FF] flex-shrink-0 mt-0.5" />
              <span><RenderMarkdownInline text={bullet} /></span>
            </li>
          ))}
        </ul>

        {joined ? (
          <div className="rounded-lg bg-[#ECFDF5] border border-[#A7F3D0] p-3 text-sm text-[#065F46]" data-testid="titanium-waitlist-confirmed">
            {founderNumber ? (
              <>
                ✓ You&apos;re <strong>founder #{founderNumber}</strong> on the Titanium waitlist. We&apos;ve emailed <strong>{email}</strong> with your founding-member benefits.
              </>
            ) : (
              <>
                ✓ You&apos;re on the Titanium waitlist. We&apos;ll email <strong>{email}</strong> the moment it&apos;s ready.
              </>
            )}
          </div>
        ) : (
          <div className="space-y-2" data-testid="titanium-waitlist-form">
            <p className="text-xs text-[#64748B] leading-relaxed">
              Open Banking + The Smith are in active development. Join the waitlist for early access and a Titanium founders&apos; price.
            </p>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-white border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
              data-testid="titanium-waitlist-email-input"
            />
            <Button
              onClick={join}
              disabled={submitting}
              className="w-full text-white"
              style={{ backgroundImage: TIER_GRADIENTS.Titanium }}
              data-testid="titanium-waitlist-submit"
            >
              {submitting ? 'Joining…' : 'Join the waitlist'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const Tier = ({ name, subtitle, price, period, highlight, features, ctaLabel, onCta, ctaDisabled, foundingOffer, annualPill }) => {
  const borderBackground = highlight ? TIER_GRADIENTS.Steel : '#E2E8F0';
  const ctaGradient = TIER_GRADIENTS[name];
  return (
    <div
      className="rounded-2xl p-[2px] flex"
      style={{
        backgroundImage: highlight ? borderBackground : undefined,
        backgroundColor: highlight ? undefined : borderBackground,
        boxShadow: highlight ? '0 0 0 4px rgba(15, 23, 42, 0.06), 0 12px 36px -18px rgba(30, 64, 175, 0.35)' : undefined,
      }}
    >
      <div className="bg-white rounded-[14px] p-7 flex flex-col w-full">
        {/* Eyebrow row — reserved at the same height across all tiers so that
            every H3 aligns horizontally regardless of whether the tier has a
            pill (Steel: Recommended, Titanium: Coming soon) or not. */}
        <div className="h-5 mb-2 flex items-start" data-testid={`pricing-eyebrow-${name.toLowerCase()}`}>
          {highlight && (
            <div
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[10px] font-bold uppercase tracking-wider leading-none"
              style={{ backgroundImage: TIER_GRADIENTS.Steel }}
              data-testid="pricing-recommended-badge"
            >
              <Sparkles className="w-3 h-3" /> Recommended
            </div>
          )}
        </div>
        <h3
          className="text-3xl font-extrabold tracking-tighter leading-none"
          style={tierGradientTextStyle(name)}
          data-testid={`pricing-tier-name-${name.toLowerCase()}`}
        >
          {name}
        </h3>
        {subtitle && (
          <div className="text-xs italic text-[#64748B] mt-1" data-testid={`pricing-subtitle-${name.toLowerCase()}`}>
            {subtitle}
          </div>
        )}
        <div className="flex items-baseline gap-1 mt-2">
          <span
            className="text-4xl font-extrabold"
            style={tierGradientTextStyle(name) || { color: '#020617' }}
            data-testid={`pricing-price-${name.toLowerCase()}`}
          >
            {price}
          </span>
          <span className="text-sm text-[#64748B]">/ {period}</span>
        </div>
        {annualPill && (
          <div
            className="mt-2 inline-flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857] text-[11px] font-semibold"
            data-testid={`pricing-annual-pill-${name.toLowerCase()}`}
          >
            {annualPill}
          </div>
        )}
        {foundingOffer}
        <ul className="mt-6 space-y-3 text-sm text-[#334155] flex-1">
          {features.map((f, idx) => (
            <li key={`${f}::${idx}`} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              <span><RenderMarkdownInline text={f} /></span>
            </li>
          ))}
        </ul>
        <Button
          disabled={ctaDisabled}
          onClick={onCta}
          className={`mt-7 w-full ${ctaDisabled ? 'bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]' : 'text-white hover:opacity-90 border-0'}`}
          style={ctaDisabled ? undefined : { backgroundImage: ctaGradient, backgroundColor: 'transparent' }}
          data-testid={`pricing-cta-${name.toLowerCase().replace(/\s+/g, '-')}`}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};

/**
 * Toggle wrapper for the Titanium column. Reads off the `titanium_live`
 * feature flag the shell already fetched — when ON, renders the regular
 * `<Tier>` with a "Forge with Titanium" Stripe CTA. When OFF, renders the
 * `<TitaniumComingSoon>` waitlist card. Single source of truth, single
 * conditional, so launch day is genuinely a flag flip and nothing else.
 */
const TitaniumOrLive = ({
  titaniumLive, price, period, billingCycle, features,
  titaniumCta, isOnTitanium, foundingOffer, annualPill,
}) => {
  if (titaniumLive) {
    return (
      <Tier
        name="Titanium"
        subtitle="Pro Premium"
        price={price}
        period={period}
        features={features}
        ctaLabel={titaniumCta.label}
        ctaDisabled={titaniumCta.disabled}
        onCta={titaniumCta.onClick}
        annualPill={annualPill}
        foundingOffer={foundingOffer}
      />
    );
  }
  return (
    <TitaniumComingSoon
      price={price}
      period={period}
      billingCycle={billingCycle}
      features={features}
      foundingOffer={foundingOffer}
      annualPill={annualPill}
    />
  );
};

const Pricing = () => {
  const [availability, setAvailability] = useState(null);
  const [pricingContent, setPricingContent] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'annual'
  const [pendingTier, setPendingTier] = useState(null); // 'steel' | 'damascus' | null — disables CTA while session is being created
  const [downgradeTarget, setDowngradeTarget] = useState(null); // 'free' | 'pro' | null — opens confirmation modal
  const [downgrading, setDowngrading] = useState(false);
  const [titaniumLive, setTitaniumLive] = useState(false);  // toggled by /admin/feature-flags
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [a, c, f] = await Promise.all([
          axios.get(`${API}/coupons/launch/availability`),
          axios.get(`${API}/page-content/pricing`),
          axios.get(`${API}/feature-flags`),
        ]);
        if (!cancelled) {
          setAvailability(a.data);
          setPricingContent(c.data);
          setTitaniumLive(!!f.data?.titanium_live);
        }
      } catch (e) {
        // Soft-fail — page still renders, just without launch banner / CMS overrides.
        console.warn('pricing data load failed', e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Handle the cancel callback Stripe sends users back to after they bail out of checkout.
  useEffect(() => {
    if (params.get('checkout') === 'cancelled') {
      toast.info('Checkout cancelled — no charge was made.', { id: 'checkout-cancelled' });
      // Clean the URL so a refresh doesn't re-fire the toast.
      params.delete('checkout');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  // Kick off Stripe Checkout. Server is the source of truth for amount/currency —
  // we only send the package_id (tier × billing cycle) + the origin so the success/
  // cancel URLs the backend builds point back to THIS site.
  const startCheckout = async (tier) => {
    if (!user) {
      toast.error('Please log in to upgrade.');
      return;
    }
    const packageId = `${tier}_${billingCycle}`;
    setPendingTier(tier);
    try {
      const r = await axios.post(
        `${API}/stripe/checkout-session`,
        { package_id: packageId, origin_url: window.location.origin },
        { withCredentials: true },
      );
      // Hand off to Stripe-hosted checkout.
      window.location.href = r.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Could not start checkout.';
      toast.error(`Checkout failed: ${detail}`);
      setPendingTier(null);
    }
  };

  const cap = availability?.cap_per_tier;
  const monthlyOff = availability?.monthly_discount_gbp;
  const annualOff = availability?.annual_discount_gbp;
  const steelRemaining = availability?.steel?.remaining ?? null;
  const damascusRemaining = availability?.damascus?.remaining ?? null;
  const titaniumRemaining = availability?.titanium?.remaining ?? null;
  // Campaign is fully exhausted only when ALL tiers have hit zero — until then the
  // banner + progress bars stay visible (sold-out tier shows "ended" copy individually).
  const offerActive = availability && (
    (steelRemaining ?? 0) > 0 ||
    (damascusRemaining ?? 0) > 0 ||
    (titaniumRemaining ?? 0) > 0
  );

  // Current-plan detection.
  const userTier = user?.tier || 'free';
  const isOnEmber = userTier === 'free';
  const isOnSteel = userTier === 'pro';
  const isOnDamascus = userTier === 'pro_plus';
  const isOnTitanium = userTier === 'pro_premium';

  // Annual saves 17% across all tiers.
  const steelPrice = billingCycle === 'annual' ? '£40' : '£4';
  const steelPeriod = billingCycle === 'annual' ? 'year' : 'month';
  const damascusPrice = billingCycle === 'annual' ? '£70' : '£7';
  const damascusPeriod = billingCycle === 'annual' ? 'year' : 'month';
  const titaniumPrice = billingCycle === 'annual' ? '£100' : '£10';
  const titaniumPeriod = billingCycle === 'annual' ? 'year' : 'month';

  // Customer-initiated downgrade. Confirmation modal first; on confirm, hit
  // /api/auth/me/downgrade which is gated to strict downgrades only.
  const confirmDowngrade = async () => {
    if (!downgradeTarget) return;
    setDowngrading(true);
    try {
      await axios.post(
        `${API}/auth/me/downgrade`,
        { tier: downgradeTarget },
        { withCredentials: true },
      );
      const label = downgradeTarget === 'free' ? 'Ember' : 'Steel';
      toast.success(`Moved to ${label}. You can re-forge any time.`);
      try { await checkAuth(); } catch (err) { console.warn('Auth refresh after checkout failed:', err); }
      setDowngradeTarget(null);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Downgrade failed.');
    } finally {
      setDowngrading(false);
    }
  };

  // Ember CTA — "Current plan" only for genuine free users; otherwise a real
  // downgrade-to-Ember option.
  const emberCta = isOnEmber
    ? { label: 'Current plan', disabled: true, onClick: undefined }
    : { label: 'Downgrade to Ember', disabled: false, onClick: () => setDowngradeTarget('free') };

  // Steel CTA — paid path for Ember, "Current plan" for Steel, real downgrade for Damascus.
  const steelCta = isOnSteel
    ? { label: 'Current plan', disabled: true, onClick: undefined }
    : isOnDamascus
      ? { label: 'Downgrade to Steel', disabled: false, onClick: () => setDowngradeTarget('pro') }
      : { label: pendingTier === 'steel' ? 'Redirecting to Stripe…' : 'Forge with Steel', disabled: pendingTier === 'steel', onClick: () => startCheckout('steel') };

  // Damascus CTA — only "Current plan" for Damascus users; Stripe path for everyone else.
  const damascusCta = isOnDamascus
    ? { label: 'Current plan', disabled: true, onClick: undefined }
    : { label: pendingTier === 'damascus' ? 'Redirecting to Stripe…' : 'Forge with Damascus', disabled: pendingTier === 'damascus', onClick: () => startCheckout('damascus') };

  // Titanium CTA — only available when the launch flag is flipped on. Otherwise
  // we fall through to the waitlist card (`TitaniumComingSoon`) below.
  const titaniumCta = isOnTitanium
    ? { label: 'Current plan', disabled: true, onClick: undefined }
    : { label: pendingTier === 'titanium' ? 'Redirecting to Stripe…' : 'Forge with Titanium', disabled: pendingTier === 'titanium', onClick: () => startCheckout('titanium') };

  return (
    <div className="min-h-screen flex flex-col" data-testid="pricing-page">
    <div className="p-6 sm:p-8 max-w-6xl mx-auto flex-1 w-full">
      {/* Back link is conditional — authed users came from the app and
          want to return to the dashboard; anonymous visitors arrived
          from the marketing site and should go back to /. */}
      <Link
        to={user ? '/dashboard' : '/'}
        className="inline-flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#020617] transition-colors mb-6"
        data-testid="pricing-back-link"
      >
        <ArrowLeft className="w-4 h-4" />
        {user ? 'Back to dashboard' : 'Back to home'}
      </Link>

      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FEF3C7] text-[#92400E] text-[10px] font-bold uppercase tracking-wider mb-4">
          <Sparkles className="w-3 h-3" />
          Pricing
        </div>
        <PageTitle
          title="Forge Ledger plans"
          subtitle="Free forever for personal use. Upgrade for emailable exports, shortfall digests, and the bits that turn Forge Ledger into a daily habit."
          className="mb-0 justify-center"
        />
        {isStaffOrAbove(user) && (
          <Link
            to="/admin/edit/pricing"
            className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold text-[#0052FF] hover:underline"
            data-testid="pricing-edit-cta"
          >
            <Settings className="w-3.5 h-3.5" />
            Edit bullet points (staff only)
          </Link>
        )}
        {/* Monthly / Annual billing toggle */}
        <div
          className="mt-6 inline-flex items-center p-1 rounded-full bg-[#F1F5F9] border border-[#E2E8F0]"
          role="tablist"
          aria-label="Billing cycle"
          data-testid="pricing-billing-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={billingCycle === 'monthly'}
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${billingCycle === 'monthly' ? 'bg-white text-[#020617] shadow-sm' : 'text-[#64748B] hover:text-[#020617]'}`}
            data-testid="pricing-billing-monthly"
          >
            Monthly
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={billingCycle === 'annual'}
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors inline-flex items-center gap-1.5 ${billingCycle === 'annual' ? 'bg-white text-[#020617] shadow-sm' : 'text-[#64748B] hover:text-[#020617]'}`}
            data-testid="pricing-billing-annual"
          >
            Annual
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857]">Save 17%</span>
          </button>
        </div>
      </div>

      {/* Founding-offer announcement banner — hidden once both tiers exhaust */}
      {offerActive && (monthlyOff || annualOff) && (
        <div
          className="max-w-3xl mx-auto mb-10 px-5 py-4 rounded-xl border border-[#FCD34D] bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] flex items-start gap-3"
          data-testid="founding-offer-banner"
        >
          <div className="w-9 h-9 rounded-full bg-[#F59E0B] text-white flex items-center justify-center flex-shrink-0">
            <Flame className="w-4 h-4" />
          </div>
          <div className="text-sm text-[#7C2D12] leading-relaxed">
            <span className="font-bold text-[#020617]">Founding-member offer — first {cap} sign-ups per paid tier.</span>{' '}
            Save <strong>£{monthlyOff?.toFixed(0)}/month for the first 6 months</strong> on monthly billing (full price after), or
            <strong> £{annualOff?.toFixed(0)} off your first year</strong> on annual.
            Applied automatically at checkout while spots last.
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6">
        <Tier
          name="Ember"
          subtitle="Free"
          price="£0"
          period="forever"
          features={pricingContent?.ember_features || []}
          ctaLabel={emberCta.label}
          ctaDisabled={emberCta.disabled}
          onCta={emberCta.onClick}
        />
        <Tier
          name="Steel"
          subtitle="Pro"
          price={steelPrice}
          period={steelPeriod}
          highlight
          features={pricingContent?.steel_features || []}
          ctaLabel={steelCta.label}
          ctaDisabled={steelCta.disabled}
          onCta={steelCta.onClick}
          annualPill={billingCycle === 'monthly' && !isOnSteel && !isOnDamascus ? '£40/year · save 17%' : null}
          foundingOffer={
            availability ? (
              <FoundingOfferProgress
                tierKey="steel"
                remaining={availability.steel.remaining}
                cap={availability.cap_per_tier}
              />
            ) : null
          }
        />
        <Tier
          name="Damascus"
          subtitle="Pro+"
          price={damascusPrice}
          period={damascusPeriod}
          features={pricingContent?.damascus_features || []}
          ctaLabel={damascusCta.label}
          ctaDisabled={damascusCta.disabled}
          onCta={damascusCta.onClick}
          annualPill={billingCycle === 'monthly' && !isOnDamascus ? '£70/year · save 17%' : null}
          foundingOffer={
            offerActive ? (
              <FoundingOfferProgress
                tierKey="damascus"
                remaining={availability.damascus.remaining}
                cap={availability.cap_per_tier}
              />
            ) : null
          }
        />
        <TitaniumOrLive
          titaniumLive={titaniumLive}
          price={titaniumPrice}
          period={titaniumPeriod}
          billingCycle={billingCycle}
          features={pricingContent?.titanium_features || []}
          titaniumCta={titaniumCta}
          isOnTitanium={isOnTitanium}
          annualPill={billingCycle === 'monthly' && !isOnTitanium ? '£100/year · save 17%' : null}
          foundingOffer={
            availability ? (
              <FoundingOfferProgress
                tierKey="titanium"
                remaining={availability.titanium?.remaining ?? null}
                cap={availability.cap_per_tier}
              />
            ) : null
          }
        />
      </div>

      {/* Forecast honesty disclaimer */}
      <div className="mt-10 max-w-3xl mx-auto text-center" data-testid="forecast-disclaimer">
        <p className="text-sm text-[#64748B] italic leading-relaxed">
          Forecasts are only as good as the data you provide. Always ensure you input
          the most accurate future bills, debts, income, etc. to get the most
          accurately <span className="text-[#0052FF] font-medium not-italic">forged</span> financial future possible.
        </p>
      </div>

      {/* VAT statement — we're under the UK VAT threshold so prices are
          shown as-is, not VAT exclusive. ICO/HMRC requires this to be
          clear so customers know they're not being surprised at checkout. */}
      <div className="mt-4 max-w-3xl mx-auto text-center" data-testid="pricing-vat-note">
        <p className="text-xs text-[#94A3B8]">
          Prices are shown in GBP. VAT not applicable.
        </p>
      </div>

      {/* Downgrade confirmation modal — single Dialog, parameterised on
          downgradeTarget ('free' or 'pro'). Surfaces what they'll lose to set
          expectations honestly before the move. */}
      <Dialog open={!!downgradeTarget} onOpenChange={(open) => { if (!open) setDowngradeTarget(null); }}>
        <DialogContent className="bg-white" data-testid="downgrade-confirm-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              <DialogTitle className="text-2xl font-bold text-[#020617]">
                {downgradeTarget === 'free' ? 'Move to Ember?' : 'Step down to Steel?'}
              </DialogTitle>
            </div>
            <DialogDescription className="text-[#64748B] pt-1">
              {downgradeTarget === 'free' ? (
                <>
                  Your forecast horizon drops back to <strong>6 months</strong>, the spending donut and auto-reconcile
                  stop working, and CSV imports go back to one-per-account. Your data stays intact — you just lose
                  Pro features.
                </>
              ) : (
                <>
                  Your forecast horizon drops from <strong>36 months</strong> to <strong>18 months</strong> and the
                  Damascus extras (multi-currency, shared accounts, early access) turn off. Steel features stay on.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-[#FEF3C7] border border-[#FCD34D] rounded-md px-3 py-2 text-sm text-[#92400E]">
            We don&apos;t issue refunds for the unused remainder — your tier swaps immediately. You can re-forge to a
            higher tier any time from this page.
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="button"
              onClick={confirmDowngrade}
              disabled={downgrading}
              className="flex-1 bg-[#020617] text-white hover:bg-[#0F172A]"
              data-testid="downgrade-confirm-button"
            >
              {downgrading ? 'Moving you…' : (downgradeTarget === 'free' ? 'Move to Ember' : 'Step down to Steel')}
            </Button>
            <Button
              type="button"
              onClick={() => setDowngradeTarget(null)}
              disabled={downgrading}
              className="flex-1 bg-[#F1F5F9] text-[#020617] hover:bg-[#E2E8F0]"
              data-testid="downgrade-cancel-button"
            >
              Stay where I am
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    <Footer variant="full" />
    </div>
  );
};

export default Pricing;
